import React, {useCallback, useState} from 'react';
import {useQuery} from "react-query";
import axios, {AxiosResponse} from "axios";
import {PendingDataGuard} from "../../commons/Indicators";
import {
    Button,
    Descriptions,
    Divider,
    Flex,
    Form,
    Image,
    Input,
    List,
    message,
    Modal,
    Select,
    Spin,
    Typography
} from "antd";
import {EditOutlined} from "@ant-design/icons";
import {useForm} from "antd/lib/form/Form";
import {defaultFormItemLayout} from "../../commons/forms";
import DescriptionsItem from "antd/es/descriptions/Item";
import AddButton from "../../commons/AddButton";
import {EnsureLoggedIn} from "../EnsureLoggedIn";
import {useTranslation} from "react-i18next";

interface AssetDto {
    id?: string;
    name: string;
    type: 'Image_PNG' | 'Image_JPEG' | 'VIDEO_MP4';
    url: string;
    category: 'ADMIN' | 'GENERAL_AVAILABILITY'
}

export const AssetsAdministration = () => {
    const {t} = useTranslation();
    const [form] = useForm<AssetDto>();
    const [editing, setEditing] = useState<boolean>(false);
    const [working, setWorking] = useState<boolean>(false);

    const {data: assets, isLoading, isError, refetch} = useQuery(
        'admin-assets',
        () => axios.get<object, AxiosResponse<AssetDto[]>>(`/api/admin/assets`)
            .then(r => r.data)
    );

    const doSubmit = useCallback((values: AssetDto) => {
        setWorking(true);
        const request = !values.id
            ? axios.post<object, AxiosResponse<AssetDto>>(`/api/admin/assets`, values)
            : axios.put<object, AxiosResponse<AssetDto>>(`/api/admin/assets/${values.id}`, values);

        request
            .then(() => {
                refetch();
                message.success('Success');
                form.resetFields();
                setEditing(false);
            })
            .catch(() => message.error('Error saving asset'))
            .finally(() => setWorking(false));
    }, [form, refetch]);

    return (
        <EnsureLoggedIn shouldBeAdmin={true}>
            <div style={{ backgroundColor: 'white', padding: '1em' }}>
                <PendingDataGuard data={assets} isLoading={isLoading} isError={isError}>
                    {working && <Spin fullscreen tip={'Working...'} />}
                    <Modal open={editing} closable destroyOnHidden={false} onCancel={() => {
                        setEditing(false);
                        form.resetFields();
                    }} footer={null}>
                        <div style={{height: '25px'}} />
                        <Form form={form} {...defaultFormItemLayout} onFinish={doSubmit}>
                            <Form.Item name={'id'} hidden />
                            <Form.Item name={'name'} label={'Name'} required rules={[{required: true}]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name={'type'} label={'Type'} required rules={[{required: true}]}>
                                <Select>
                                    <Select.Option value="Image_PNG">Image PNG</Select.Option>
                                    <Select.Option value="Image_JPEG">Image JPEG</Select.Option>
                                    <Select.Option value="VIDEO_MP4">Video MP4</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item name={'category'} label={'Category'} required rules={[{required: true}]}>
                                <Select>
                                    <Select.Option value="ADMIN">Admin</Select.Option>
                                    <Select.Option value="GENERAL_AVAILABILITY">General Availability</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item name={'url'} label={'URL'} required rules={[{required: true, type: 'url'}]}>
                                <Input inputMode={"url"} />
                            </Form.Item>
                            <Flex vertical={false} justify={'end'}>
                                <Button type={"primary"} onClick={form.submit}>Save</Button>
                            </Flex>
                        </Form>
                    </Modal>

                    <List
                        header={<AddButton onSave={() => {
                            form.resetFields();
                            setEditing(true);
                        }} />}
                        size={"small"}
                        dataSource={assets || []}
                        style={{maxWidth: '100%'}}
                        renderItem={(asset) => (
                            <div style={{maxWidth: '100%'}}>
                                <Descriptions bordered size={"small"} title={asset.name} layout={"vertical"}>
                                    <DescriptionsItem label={<Typography.Text strong>Type</Typography.Text>}>
                                        <Typography.Paragraph>{asset.type}</Typography.Paragraph>
                                    </DescriptionsItem>
                                    <DescriptionsItem label={<Typography.Text strong>Category</Typography.Text>}>
                                        <Typography.Paragraph>{asset.category}</Typography.Paragraph>
                                    </DescriptionsItem>
                                    <DescriptionsItem label={<Typography.Text strong>URL</Typography.Text>}>
                                        <Flex justify={'center'}>
                                            <Image preview src={asset.url} style={{maxHeight: '100px'}} />
                                        </Flex>
                                    </DescriptionsItem>
                                    <DescriptionsItem >
                                        <Button
                                            type={"primary"}
                                            icon={<EditOutlined />}
                                            onClick={() => {
                                                form.setFieldsValue(asset);
                                                setEditing(true);
                                            }}
                                            size={"large"}
                                            style={{width: '100%'}}
                                        >
                                            {t('common.edit')}
                                        </Button>
                                    </DescriptionsItem>
                                </Descriptions>
                                <Divider />
                            </div>)}
                    />
                </PendingDataGuard>
            </div>
        </EnsureLoggedIn>
    );
};
